import { FunctionComponent, useState } from 'react';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useNotification } from '@starjack/features/notifications';
import { Drawer, Stack, Typography } from '@mui/material';
import { ConditionPreviewList } from './ConditionPreviewList';
import { ConditionLink, ContingentCondition } from '@/models/conditionLink';
import {
    useGetConditionLinkQuery,
    usePostOrPutConditionLinkMutation,
} from '@/api/v2/conditionLinks';
import { ContingentConditionFormContainer } from '@/pages/rulesets/components/conditions/ContingentConditionFormContainer';
import { ConfirmationDialog } from '@/pages/rulesets/components/ConfirmationDialog';

type ContingentConditionConfigurationProps = {
    conditionLink: ConditionLink;
    onUpdateConditionLink: (condition: ConditionLink) => void;
};

export const ContingentConditionConfiguration: FunctionComponent<
    ContingentConditionConfigurationProps
> = ({ conditionLink, onUpdateConditionLink }) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { isLoading: getQueryLoading } = useGetConditionLinkQuery({
        liftOperatorId,
        id: conditionLink.id,
    });
    const [apiAction, { isLoading: updateLoading }] =
        usePostOrPutConditionLinkMutation({
            liftOperatorId,
            conditionLink,
        });
    const [conditionToDelete, setConditionToDelete] =
        useState<ContingentCondition | null>(null);

    const isLoading = getQueryLoading || updateLoading;
    const { addNotification } = useNotification();

    const [conditionDrawerConfig, setConditionDrawerConfig] = useState<{
        isOpen: boolean;
        conditionTemplate: ContingentCondition;
        options: {
            insertAtIndex?: number;
        } | null;
    }>({
        isOpen: false,
        conditionTemplate: null,
        options: null,
    });

    const bulkUpdateConditions = (
        newConditionsSorted: ContingentCondition[]
    ) => {
        apiAction({
            liftOperatorId,
            conditionLink: {
                ...conditionLink,
                contingentConditions: newConditionsSorted.map((c, index) => ({
                    ...c,
                    sort: index,
                })),
            },
        })
            .unwrap()
            .then((conditionLink) => {
                addNotification({
                    message: 'Sortierung der Regel aktualisiert',
                    type: 'success',
                });

                onUpdateConditionLink(conditionLink);
            })
            .catch(() => {
                addNotification({
                    message:
                        'Fehler beim Speichern der neuen Sortierung. Bitte später noch einmal versuchen',
                    type: 'error',
                });
            });
    };

    const updateConditionSort = (
        conditions: ContingentCondition[],
        condition: ContingentCondition,
        priorityChange: 'increase' | 'decrease'
    ) => {
        const cToUpdateIndex = conditions.findIndex(
            (c) => c.id === condition.id
        );

        if (cToUpdateIndex > -1) {
            const cToReplacedIndex =
                priorityChange === 'increase'
                    ? cToUpdateIndex - 1
                    : cToUpdateIndex + 1;

            if (conditions[cToReplacedIndex]) {
                const conditionToUpdate = { ...conditions[cToUpdateIndex] };
                const conditionToReplace = { ...conditions[cToReplacedIndex] };
                const toReplaceSort = conditionToReplace.sort;
                const toUpdateSort = conditionToUpdate.sort;

                conditions.splice(cToUpdateIndex, 1, {
                    ...conditionToUpdate,
                    sort: toReplaceSort,
                });

                conditions.splice(cToReplacedIndex, 1, {
                    ...conditionToReplace,
                    sort: toUpdateSort,
                });

                apiAction({
                    liftOperatorId,
                    conditionLink: {
                        ...conditionLink,
                        contingentConditions: [...conditions],
                    },
                })
                    .unwrap()
                    .then((conditionLink) => {
                        addNotification({
                            message: 'Sortierung der Regel aktualisiert',
                            type: 'success',
                        });

                        onUpdateConditionLink(conditionLink);
                    })
                    .catch(() => {
                        addNotification({
                            message:
                                'Fehler beim Speichern der neuen Sortierung. Bitte später noch einmal versuchen',
                            type: 'error',
                        });
                    });
            }
        }
    };

    const deleteCondition = (condition: ContingentCondition) => {
        const conditions = [...conditionLink.contingentConditions];
        const conditionIndex = conditions.findIndex(
            (c) => c.id === condition.id
        );

        if (conditionIndex > -1) {
            conditions.splice(conditionIndex, 1);

            apiAction({
                liftOperatorId,
                conditionLink: {
                    ...conditionLink,
                    contingentConditions: [...conditions],
                },
            })
                .unwrap()
                .then((conditionLink) => {
                    addNotification({
                        message: 'Regel gelöscht',
                        type: 'success',
                    });
                    setConditionToDelete(null);
                    onUpdateConditionLink(conditionLink);
                })
                .catch(() => {
                    addNotification({
                        message: 'Fehler beim Löschen der Regel',
                        type: 'error',
                    });
                });
        }
    };

    return (
        <Stack spacing={3}>
            <Typography gutterBottom>
                Es lassen sich mehrere Verfügbarkeits-Regeln hinterlegen. <br />
                Die Regeln kommen der Priorität nach zur Anwendung.
            </Typography>

            <Typography>
                Bitte beachten: Ein in der „allgemeinen Konfiguration“
                hinterlegtes Kontingent braucht sich durch diese Regeln
                ebenfalls auf.
            </Typography>

            <ConditionPreviewList
                conditions={conditionLink.contingentConditions}
                onCreateOrUpdate={(condition: ContingentCondition, options) => {
                    setConditionDrawerConfig({
                        isOpen: true,
                        conditionTemplate: condition || null,
                        options: options
                            ? {
                                  insertAtIndex: options.insertAtIndex,
                              }
                            : null,
                    });
                }}
                onDelete={(condition: ContingentCondition) => {
                    setConditionToDelete(condition);
                }}
                onBulkUpdateConditions={bulkUpdateConditions}
                onPriorityChange={updateConditionSort}
                isLoading={isLoading}
                onAddClick={(options) => {
                    setConditionDrawerConfig({
                        isOpen: true,
                        conditionTemplate: null,
                        options: options
                            ? {
                                  insertAtIndex: options?.insertAtIndex,
                              }
                            : null,
                    });
                }}
            />

            <Drawer
                onClose={() => {
                    setConditionDrawerConfig({
                        isOpen: false,
                        conditionTemplate: null,
                        options: null,
                    });
                }}
                anchor='right'
                open={conditionDrawerConfig.isOpen}
            >
                <ContingentConditionFormContainer
                    onClose={() => {
                        setConditionDrawerConfig((prevState) => ({
                            ...prevState,
                            isOpen: false,
                        }));
                    }}
                    createNewConditionOptions={
                        conditionDrawerConfig.options
                            ? {
                                  insertAtIndex:
                                      conditionDrawerConfig.options
                                          .insertAtIndex,
                              }
                            : undefined
                    }
                    condition={conditionDrawerConfig.conditionTemplate}
                    conditionLink={conditionLink}
                    onUpdateConditionLink={onUpdateConditionLink}
                />
            </Drawer>

            <ConfirmationDialog
                open={!!conditionToDelete}
                body='Soll die Regel wirklich gelöscht werden?'
                confirmButtonText='Regel löschen'
                onClose={() => {
                    setConditionToDelete(null);
                }}
                onConfirm={() => {
                    deleteCondition(conditionToDelete);
                }}
                deleteLoading={isLoading}
            />
        </Stack>
    );
};
